/**
 * Ara Digital Marketing - AI Chatbot Configuration
 * Defines all CTA options, bot responses, and conversation flows
 */

// Main CTA menu options shown on chatbot open
export const MAIN_CTA_OPTIONS = [
  {
    id: "website_services",
    label: "Website Services",
    icon: "language",
    description: "Custom websites, landing pages & e-commerce solutions",
  },
  {
    id: "digital_marketing",
    label: "Digital Marketing",
    icon: "campaign",
    description: "Google Ads, Meta Ads & performance marketing",
  },
  {
    id: "seo_plans",
    label: "SEO Plans",
    icon: "trending_up",
    description: "On-page, off-page SEO & keyword strategy",
  },
  {
    id: "social_media",
    label: "Social Media",
    icon: "share",
    description: "Content creation, management & growth strategy",
  },
  {
    id: "contact_us",
    label: "Contact Us",
    icon: "call",
    description: "Talk to our digital marketing experts",
  },
];

// Bot auto-responses for each CTA option
export const BOT_RESPONSES = {
  website_services: {
    message:
      "Great choice! Ara Digital Marketing offers end-to-end website solutions. Here's what we can do for you:",
    options: [
      { id: "ws_business", label: "Business Website", icon: "web" },
      { id: "ws_ecommerce", label: "E-Commerce Store", icon: "shopping_cart" },
      { id: "ws_landing", label: "Landing Pages", icon: "description" },
      { id: "ws_redesign", label: "Website Redesign", icon: "brush" },
    ],
  },
  digital_marketing: {
    message:
      "Let's boost your brand online! Our digital marketing services include:",
    options: [
      { id: "dm_google", label: "Google Ads (PPC)", icon: "ads_click" },
      { id: "dm_meta", label: "Meta / Facebook Ads", icon: "thumb_up" },
      { id: "dm_email", label: "Email Marketing", icon: "email" },
      { id: "dm_analytics", label: "Analytics & Reports", icon: "bar_chart" },
    ],
  },
  seo_plans: {
    message:
      "Rank higher, get found faster! Our SEO services are tailored to your business needs:",
    options: [
      { id: "seo_audit", label: "Free SEO Audit", icon: "search" },
      { id: "seo_onpage", label: "On-Page SEO", icon: "code" },
      { id: "seo_offpage", label: "Off-Page SEO", icon: "link" },
      { id: "seo_local", label: "Local SEO / GMB", icon: "location_on" },
    ],
  },
  social_media: {
    message:
      "Build your brand presence across all platforms! We handle everything:",
    options: [
      { id: "sm_content", label: "Content Creation", icon: "create" },
      { id: "sm_management", label: "Account Management", icon: "manage_accounts" },
      { id: "sm_reels", label: "Reels & Videos", icon: "videocam" },
      { id: "sm_strategy", label: "Growth Strategy", icon: "insights" },
    ],
  },
  contact_us: {
    message:
      "We'd love to hear from you! Here's how you can reach our team at Ara Digital Marketing:",
    options: [
      { id: "ct_call", label: "Request Callback", icon: "phone_callback" },
      { id: "ct_whatsapp", label: "WhatsApp Us", icon: "chat" },
      { id: "ct_email", label: "Send Email", icon: "mail" },
      { id: "ct_meeting", label: "Book a Meeting", icon: "event" },
    ],
  },
};

// Deeper sub-responses for second-level CTA clicks
export const SUB_RESPONSES = {
  // Website Services sub-responses
  ws_business:
    "Our business websites start at competitive prices with responsive design, SEO-ready structure, and fast loading speed. Want us to share a proposal?",
  ws_ecommerce:
    "We build powerful e-commerce stores on Shopify, WooCommerce & custom platforms. Includes payment gateway, inventory management & more!",
  ws_landing:
    "High-converting landing pages designed for your ad campaigns. A/B tested designs with clear CTAs. Perfect for lead generation!",
  ws_redesign:
    "Give your old website a modern makeover! We'll improve UI/UX, speed, SEO, and mobile responsiveness.",

  // Digital Marketing sub-responses
  dm_google:
    "Our Google Ads experts will set up, optimize & manage your PPC campaigns for maximum ROI. We handle Search, Display, Shopping & YouTube ads.",
  dm_meta:
    "Reach your target audience on Facebook & Instagram with precision targeting, creative ads, and data-driven optimization.",
  dm_email:
    "Automated email campaigns that convert! From newsletters to drip campaigns, we'll nurture your leads into customers.",
  dm_analytics:
    "Get detailed monthly reports with insights on traffic, conversions, ROI, and actionable recommendations.",

  // SEO sub-responses
  seo_audit:
    "Get a free comprehensive SEO audit of your website! We'll analyze technical issues, content gaps, and ranking opportunities.",
  seo_onpage:
    "We optimize your meta tags, content structure, internal linking, site speed, and schema markup for better rankings.",
  seo_offpage:
    "Quality backlink building, guest posting, directory submissions, and authority building to strengthen your domain.",
  seo_local:
    "Dominate local search results! We'll optimize your Google My Business, local citations, and review management.",

  // Social Media sub-responses
  sm_content:
    "Eye-catching posts, carousels, stories & graphics tailored for your brand. We create content that engages and converts!",
  sm_management:
    "Full account management including posting schedule, community management, hashtag strategy & performance tracking.",
  sm_reels:
    "Trending reels, shorts & video content that boost engagement and help you go viral. Professional editing included!",
  sm_strategy:
    "Custom growth roadmap for your brand with competitor analysis, audience targeting & content calendar.",

  // Contact sub-responses
  ct_call:
    "We'll have one of our digital marketing experts call you within 24 hours. Please share your name and phone number!",
  ct_whatsapp:
    "Chat with us on WhatsApp for quick responses! Our team is available Mon-Sat, 10 AM to 7 PM.",
  ct_email:
    "Drop us an email at hello@aradigital.com and we'll get back to you within a few hours!",
  ct_meeting:
    "Book a free 30-minute strategy call with our experts. We'll discuss your goals and create a custom plan!",
};

// Greeting message
export const GREETING = {
  title: "Ara Digital",
  subtitle: "Marketing Assistant",
  message:
    "Hi there! I'm your Ara Digital Marketing assistant. I'm here to help you grow your business online. What would you like to explore?",
};

// Fallback response for free-text input
export const FALLBACK_RESPONSE =
  "Thanks for your message! Our team at Ara Digital Marketing will review your query and get back to you shortly. Meanwhile, feel free to explore our services below:";
